// src/CategoryFacetChips.tsx
import { useMemo } from "react";
import Box from "@mui/material/Box/index.js";
import Chip from "@mui/material/Chip/index.js";
import Typography from "@mui/material/Typography/index.js";
import { jsx, jsxs } from "react/jsx-runtime";
var buildFacetTree = (facets, categories) => {
  const categoryById = new Map(
    categories.map((category) => [String(category.id), category])
  );
  const roots = /* @__PURE__ */ new Map();
  facets.forEach((facet) => {
    const chain = [];
    let current = categoryById.get(facet.categoryId);
    while (current) {
      chain.unshift(current);
      current = current.parentId != null ? categoryById.get(String(current.parentId)) : void 0;
    }
    if (chain.length === 0) {
      chain.push({ id: facet.categoryId, name: facet.categoryName });
    }
    let level = roots;
    chain.forEach((ancestor, index) => {
      const key = String(ancestor.id);
      if (!level.has(key)) {
        level.set(key, {
          id: key,
          name: ancestor.name,
          imageSrc: ancestor.imageSrc,
          count: 0,
          children: /* @__PURE__ */ new Map()
        });
      }
      const node = level.get(key);
      node.count += facet.count;
      if (index === chain.length - 1) {
        node.categoryName = facet.categoryName;
      }
      level = node.children;
    });
  });
  const toArray = (level) => [...level.values()].map((node) => ({ ...node, children: toArray(node.children) })).sort((a, b) => b.count - a.count);
  return toArray(roots);
};
var CategoryFacetTreeNode = ({
  node,
  onSelectLeaf,
  depth
}) => {
  const isLeaf = node.children.length === 0;
  if (isLeaf) {
    return /* @__PURE__ */ jsx(
      Chip,
      {
        label: `${node.name} (${node.count})`,
        onClick: () => onSelectLeaf(node.categoryName ?? node.name ?? "")
      }
    );
  }
  const leafChildren = node.children.filter(
    (child) => child.children.length === 0
  );
  const groupChildren = node.children.filter(
    (child) => child.children.length > 0
  );
  return /* @__PURE__ */ jsxs(
    Box,
    {
      sx: {
        display: "flex",
        flexDirection: "column",
        gap: 1,
        ...depth > 0 && {
          pl: 1.5,
          borderLeft: "2px solid",
          borderColor: "divider"
        }
      },
      children: [
        depth === 0 ? /* @__PURE__ */ jsxs(
          Box,
          {
            sx: {
              display: "flex",
              alignItems: "center",
              gap: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              padding: 1.5,
              bgcolor: "alternate.main"
            },
            children: [
              /* @__PURE__ */ jsx(
                Box,
                {
                  component: "img",
                  src: node.imageSrc || "/assets/niezdjecie.png",
                  alt: node.name,
                  sx: {
                    width: 56,
                    height: 56,
                    borderRadius: 1,
                    objectFit: "cover",
                    flexShrink: 0
                  }
                }
              ),
              /* @__PURE__ */ jsxs(Typography, { variant: "subtitle1", fontWeight: 700, children: [
                node.name,
                " (",
                node.count,
                ")"
              ] })
            ]
          }
        ) : /* @__PURE__ */ jsxs(Typography, { variant: "body2", color: "text.secondary", fontWeight: 600, children: [
          node.name,
          " (",
          node.count,
          ")"
        ] }),
        leafChildren.length > 0 && /* @__PURE__ */ jsx(Box, { sx: { display: "flex", flexWrap: "wrap", gap: 1 }, children: leafChildren.map((child) => /* @__PURE__ */ jsx(
          Chip,
          {
            label: `${child.name} (${child.count})`,
            onClick: () => onSelectLeaf(child.categoryName ?? child.name ?? "")
          },
          child.id
        )) }),
        groupChildren.length > 0 && /* @__PURE__ */ jsx(Box, { sx: { display: "flex", flexDirection: "column", gap: 1.5 }, children: groupChildren.map((child) => /* @__PURE__ */ jsx(
          CategoryFacetTreeNode,
          {
            node: child,
            onSelectLeaf,
            depth: depth + 1
          },
          child.id
        )) })
      ]
    }
  );
};
var CategoryFacetChips = ({
  facets,
  categories,
  onSelectLeaf
}) => {
  const tree = useMemo(
    () => buildFacetTree(facets, categories ?? []),
    [facets, categories]
  );
  if (tree.length === 0) return null;
  return /* @__PURE__ */ jsx(Box, { sx: { display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }, children: tree.map((node) => /* @__PURE__ */ jsx(
    CategoryFacetTreeNode,
    {
      node,
      onSelectLeaf,
      depth: 0
    },
    node.id
  )) });
};

export {
  CategoryFacetChips
};
